import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dataApi } from '../../services/api';
import { QUOTE_REQUEST_STATUSES } from '../../lib/quoteRequestStatus';
import { canDelete } from '../../lib/records';
import DeleteConfirmDialog from '../../components/dialogs/DeleteConfirmDialog';
import * as appwrite from '../../services/appwrite';
import { COLLECTIONS } from '../../services/appwrite';
import { toast } from 'sonner';
import ImportPanelQuoteForm from '../overlays/ImportPanelQuoteForm';
import CustomerForm from '../overlays/CustomerForm';
import VehicleForm from '../overlays/VehicleForm';
import PreviewImageThumbnails from '../../components/PreviewImageThumbnails';
import { ChevronRight, Import, Maximize2, Plus, Trash2 } from 'lucide-react';

import PageHeaderActions from '../../components/PageHeaderActions';
import ListToolbar from '../../components/ListToolbar';
import { useObfuscate } from '../../hooks/useObfuscate';
import { cn } from '../../lib/utils';

// shadcn UI components
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { QuoteStatusBadge } from '../../components/ui/quote-status-badge';
import { SegmentedControl } from '../../components/ui/segmented';
import { Skeleton } from '../../components/ui/skeleton';

// customer_organisations store phones/emails as JSON string arrays — pull the first
const firstOfJsonArray = (val) => {
  if (!val) return '';
  if (Array.isArray(val)) return val[0] || '';
  try {
    const arr = JSON.parse(val);
    return Array.isArray(arr) ? (arr[0] || '') : (val || '');
  } catch {
    return val || '';
  }
};

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'customer', label: 'Customer name' }
];

const WORK_OPTIONS = [
  { value: 'all', label: 'All work' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'private', label: 'Private' }
];

const DEFAULT_LIST_VIEW = { sort: 'newest', work: 'all' };

// Sort and filter are remembered between visits; the search is not, so coming
// back to the page never opens on a list narrowed by something long forgotten.
const loadListView = () => {
  try {
    return { ...DEFAULT_LIST_VIEW, ...JSON.parse(localStorage.getItem('quoteListView') || '{}') };
  } catch {
    return DEFAULT_LIST_VIEW;
  }
};

const customerDisplayName = (quote) => {
  if (quote?.customerOrganisation) return quote.customerOrganisation.name || '';
  const c = quote?.customer;
  if (!c) return '';
  return `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.trim();
};

const QuoteRequests = () => {
  const navigate = useNavigate();
  const priv = useObfuscate();
  const [searchParams] = useSearchParams();

  // ?status= from a dashboard tile. It wins over the cached filter for that
  // first render only: arriving from "3 new" and landing on whatever was last
  // filtered here is the sort of thing that makes a number look broken.
  const statusFromUrl = searchParams.get('status');
  const initialStatus =
    statusFromUrl && (statusFromUrl === 'all' || QUOTE_REQUEST_STATUSES.includes(statusFromUrl))
      ? statusFromUrl
      : null;

  // Load the cached status filter from localStorage. Only the status is read
  // back: older caches also hold a search, from before the list had a search
  // box to clear it with.
  const loadCachedFilters = () => {
    const defaults = { status: initialStatus || 'all' };
    try {
      const cached = localStorage.getItem('dashboardFilters');
      if (!cached) return defaults;
      const parsed = JSON.parse(cached);
      return { status: initialStatus || parsed.status || 'all' };
    } catch {
      return defaults;
    }
  };

  const loadCachedQuoteId = () => {
    try {
      const cached = localStorage.getItem('selectedQuoteId');
      return cached;
    } catch {
      return null;
    }
  };

  const [quotes, setQuotes] = useState([]);
  const [insurers, setInsurers] = useState([]); // { id, name }
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [filters, setFilters] = useState(loadCachedFilters());
  const [search, setSearch] = useState('');
  const [listView, setListView] = useState(loadListView);
  const hasRestoredState = useRef(false);
  const selectedItemRef = useRef(null);
  
  // New state for bulk operations and modal
  const [selectedQuoteRequests, setSelectedQuoteRequests] = useState(new Set());
  const [creatingQuoteRequest, setCreatingQuoteRequest] = useState(false);
  const [showImportPanelQuoteForm, setShowImportPanelQuoteForm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  // The rows a bulk delete will act on, while its confirmation is open.
  const [pendingBulkDelete, setPendingBulkDelete] = useState(null);
  const [panelQuoteEnabled, setPanelQuoteEnabled] = useState(false);
  
  // Search dialog state
  const [searchCustomerId, setSearchCustomerId] = useState(null);
  const [searchCustomerOpen, setSearchCustomerOpen] = useState(false);
  const [searchVehicleId, setSearchVehicleId] = useState(null);
  const [searchVehicleOpen, setSearchVehicleOpen] = useState(false);

  const handleSearchCustomerSelect = useCallback((customer) => {
    setSearchCustomerId(customer.$id);
    setSearchCustomerOpen(true);
  }, []);

  const handleSearchVehicleSelect = useCallback((vehicle) => {
    setSearchVehicleId(vehicle.$id);
    setSearchVehicleOpen(true);
  }, []);

  const statusFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'new', label: 'New' },
    { value: 'drafting', label: 'Drafting' },
    { value: 'awaiting response', label: 'Awaiting Response' },
    { value: 'accepted', label: 'Accepted' }
  ];

  const fetchQuotes = async (shouldRestoreSelection = true) => {
    try {
      if (initialLoading) {
        setInitialLoading(true);
      } else {
        setListLoading(true);
      }
      
      // Status is filtered by the query; search, sort and work type are
      // applied to the loaded list below, so typing never refetches.
      const apiFilters = {};
      if (filters.status && filters.status !== 'all') {
        apiFilters.status = filters.status;
      }
      
      const quotesList = await dataApi.quoteRequestList(apiFilters);
      setQuotes(quotesList || []);
      
      if (shouldRestoreSelection && quotesList && quotesList.length > 0) {
        // Get the cached quote ID fresh each time (in case user came back from details page)
        const cachedQuoteId = loadCachedQuoteId();
        
        // Try to restore cached selection first
        if (cachedQuoteId && quotesList.some(q => String(q.$id) === String(cachedQuoteId))) {
          // Always restore cached selection on initial load, or if it's different
          if (!hasRestoredState.current || !selectedQuote || String(selectedQuote.$id) !== String(cachedQuoteId)) {
            await handleQuoteSelect(cachedQuoteId);
            hasRestoredState.current = true;
          }
        } else if (!hasRestoredState.current) {
          // Only auto-select first if we haven't restored yet
          await handleQuoteSelect(quotesList[0].$id);
          hasRestoredState.current = true;
        }
      }
    } catch (error) {
      toast.error('Failed to fetch quote requests');
      console.error('Error fetching quotes:', error);
    } finally {
      setInitialLoading(false);
      setListLoading(false);
    }
  };

  // Load insurers once for display resolution
  useEffect(() => {
    const loadInsurers = async () => {
      try {
        const data = await dataApi.insurerList();
        const list = (data || [])
          .filter(ins => ins?.$id && ins?.name)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(ins => ({ id: ins.$id, name: ins.name }));
        setInsurers(list);
      } catch (err) {
        console.error('Failed to load insurers', err);
      }
    };
    loadInsurers();
  }, []);

  const handleQuoteSelect = async (quoteId) => {
    try {
      setPreviewLoading(true);
      const quoteData = await dataApi.quoteRequestGet(quoteId);
      setSelectedQuote(quoteData);
      
      // Cache the selected quote ID (keep as string, don't convert)
      localStorage.setItem('selectedQuoteId', String(quoteId));
    } catch (error) {
      console.error('Error fetching quote request details:', error);
      showErrorToast('Failed to load quote preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Remember the request as the selected one before leaving, so coming back
  // to the list lands on the request that was just open rather than the one
  // last clicked.
  const openQuoteRequest = (quoteId) => {
    localStorage.setItem('selectedQuoteId', String(quoteId));
    navigate(`/quote-requests/${quoteId}`);
  };

  const handleExpandQuote = () => {
    if (selectedQuote) {
      openQuoteRequest(selectedQuote.$id);
    }
  };

  // Handle bulk selection
  const handleSelectQuoteRequest = (quoteId, checked) => {
    const newSelected = new Set(selectedQuoteRequests);
    if (checked) {
      newSelected.add(quoteId);
    } else {
      newSelected.delete(quoteId);
    }
    setSelectedQuoteRequests(newSelected);
  };

  const insurerMap = useMemo(() => {
    const map = new Map();
    insurers.forEach(ins => map.set(ins.id, ins.name));
    return map;
  }, [insurers]);

  const resolveInsurerDisplay = (quote) => {
    const { insuranceCompany, otherInsurer } = quote || {};
    if (insuranceCompany && insurerMap.has(insuranceCompany)) {
      return insurerMap.get(insuranceCompany);
    }
    if (insuranceCompany) {
      const byName = insurers.find(ins => ins.name === insuranceCompany);
      if (byName) return byName.name;
      return insuranceCompany;
    }
    return otherInsurer || 'Insurance work';
  };

  // Helper functions for toast messages
  const showSuccessToast = (message) => {
    toast.success(message);
  };

  const showErrorToast = (message) => {
    toast.error(message);
  };

  // Handle bulk delete. Deleting is soft — a request can be restored for 90
  // days — but it still honours each row's own delete permission: requests a
  // customer submitted can only be deleted by the organisation owner.
  const handleBulkDelete = async () => {
    if (selectedQuoteRequests.size === 0) return;

    const user = await appwrite.getCurrentUser();
    const chosen = quotes.filter((quote) => selectedQuoteRequests.has(quote.$id));
    const deletable = chosen.filter((quote) => canDelete(quote, user));
    const skipped = selectedQuoteRequests.size - deletable.length;

    if (deletable.length === 0) {
      toast.error('Only the organisation owner can delete requests customers submitted');
      return;
    }

    setPendingBulkDelete({ rows: deletable, skipped });
  };

  const confirmBulkDelete = async () => {
    const { rows: deletable, skipped } = pendingBulkDelete;
    setBulkDeleteLoading(true);
    try {
      for (const quote of deletable) {
        await dataApi.quoteRequestDelete(quote);
      }

      toast.success(`${deletable.length} quote request(s) sent to the recycle bin`);
      if (skipped) {
        toast.info(`${skipped} not deleted — only the organisation owner can delete requests customers submitted`);
      }
      setSelectedQuoteRequests(new Set());

      // If the currently selected quote was deleted, clear it
      if (selectedQuote && deletable.some((quote) => quote.$id === selectedQuote.$id)) {
        setSelectedQuote(null);
        localStorage.removeItem('selectedQuoteId');
      }

      // Refresh the quotes list (don't restore selection since we just deleted)
      await fetchQuotes(false);
    } catch (error) {
      console.error('Error deleting quote requests:', error);
      const errorMessage = error.message || 'Failed to delete quote requests';
      toast.error(errorMessage);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  // Create a blank quote request and go straight to the collaborative form —
  // customer, vehicle, photos and details are all filled in there
  const handleCreateQuoteRequest = async () => {
    if (creatingQuoteRequest) return;
    setCreatingQuoteRequest(true);
    try {
      const quoteRequest = await dataApi.quoteRequestCreate({
        customerId: '', // linked later from the quote request page
        status: 'new',
        damageAssessmentStatus: 'pending'
      });
      localStorage.setItem('selectedQuoteId', String(quoteRequest.$id));
      navigate(`/quote-requests/${quoteRequest.$id}`);
    } catch (error) {
      console.error('Error creating quote request:', error);
      toast.error(error.message || 'Failed to create quote request');
      setCreatingQuoteRequest(false);
    }
  };

  // Handle Panel Quote import success
  const handleImportPanelQuoteSuccess = (result) => {
    setShowImportPanelQuoteForm(false);
    showSuccessToast(result.message || 'Panel Quote job imported successfully!');
    fetchQuotes(false); // Refresh the list
  };

  useEffect(() => {
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // On component mount, ensure we restore the cached state
  useEffect(() => {
    // Reset the restoration flag when component mounts
    hasRestoredState.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs once on mount

  // Realtime subscription: refresh list when quote_requests are created or updated
  useEffect(() => {
    const refreshTimer = { current: null };
    const debouncedRefresh = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        fetchQuotes(true);
      }, 1500);
    };

    const unsubscribe = appwrite.subscribeToCollection(
      COLLECTIONS.QUOTE_REQUESTS,
      (response) => {
        const events = response.events || [];
        const isRelevant = events.some(
          (e) => e.includes('.create') || e.includes('.update')
        );
        if (isRelevant) {
          debouncedRefresh();
        }
      }
    );

    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bring the selected request into view — coming back from a request in
  // particular, when it can be well down the list.
  useEffect(() => {
    selectedItemRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedQuote?.$id, initialLoading]);

  // Load Panel Quote settings once to control visibility of the import action
  useEffect(() => {
    let mounted = true;
    const loadPanelQuoteSettings = async () => {
      try {
        const settings = await dataApi.panelQuoteSettingsGet();
        if (!mounted) return;
        setPanelQuoteEnabled(!!(settings?.enabled && settings?.hasPassword));
      } catch {
        if (!mounted) return;
        setPanelQuoteEnabled(false);
      }
    };

    loadPanelQuoteSettings();
    return () => {
      mounted = false;
    };
  }, []);

  const handleFilterChange = (field, value) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    setFilters(newFilters);
    
    // Cache the filters
    localStorage.setItem('dashboardFilters', JSON.stringify(newFilters));
    
    // Reset restoration flag when filters change so we can restore cached selection with new filter
    hasRestoredState.current = false;
  };

  const getRequestNumber = (quote) => {
    const raw = quote?.quoteRequestNumber
      ?? quote?.quote_request_number
      ?? quote?.requestNumber
      ?? quote?.request_number
      ?? quote?.$sequence;

    const numeric = parseInt(raw, 10);
    const padded = Number.isFinite(numeric)
      ? String(Math.max(0, numeric)).padStart(5, '0')
      : String(raw || '0').padStart(5, '0');

    return `QR-${padded}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return `$${number.toFixed(2)}`;
  };

  const getVehicleDescription = (quote) => {
    // Prefer summary fields if present
    const summary = quote?.vehicleSummary || quote?.vehicle_summary;
    if (summary) return summary;

    const primaryVehicle = quote?.vehicle
      || quote?.vehicleDetails
      || quote?.vehicle_details
      || quote?.vehicleData
      || quote?.vehicle_data;

    const customerVehicle = quote?.customerVehicle || quote?.customer_vehicle;
    const customerVehicleInner = customerVehicle?.vehicle || customerVehicle?.vehicleDetails || customerVehicle?.vehicle_details;

    const vehicleCandidate = Array.isArray(primaryVehicle) ? primaryVehicle[0]
      : primaryVehicle || customerVehicleInner || customerVehicle || {};

    const year = vehicleCandidate?.year || vehicleCandidate?.Year || quote?.vehicleYear || quote?.vehicle_year || customerVehicle?.year;
    const make = vehicleCandidate?.make || vehicleCandidate?.Make || quote?.vehicleMake || quote?.vehicle_make || customerVehicle?.make;
    let model = vehicleCandidate?.model || vehicleCandidate?.Model || quote?.vehicleModel || quote?.vehicle_model || customerVehicle?.model;
    
    // When model is 'Other', display the custom otherModel value instead
    if (model === 'Other') {
      const otherModel = vehicleCandidate?.otherModel || vehicleCandidate?.other_model || quote?.vehicleOtherModel || quote?.vehicle_other_model || customerVehicle?.otherModel;
      if (otherModel) model = otherModel;
    }
    
    const rego = priv.rego(vehicleCandidate?.registration
      || vehicleCandidate?.rego
      || vehicleCandidate?.Registration
      || vehicleCandidate?.registrationNumber
      || quote?.vehicleRegistration
      || quote?.vehicle_registration
      || quote?.vehicleRego
      || quote?.vehicle_rego
      || customerVehicle?.registration
      || customerVehicle?.rego);

    const descriptionText = quote?.vehicleDescription || quote?.vehicle_description || vehicleCandidate?.description;

    const parts = [year, make, model].filter(Boolean);
    const base = parts.length > 0 ? parts.join(' ') : descriptionText || rego;

    if (rego && base) {
      return `${base} • ${rego}`;
    }

    return base || rego || ''; // empty string prevents fallback text
  };

  const updateListView = (patch) => {
    const next = { ...listView, ...patch };
    setListView(next);
    localStorage.setItem('quoteListView', JSON.stringify(next));
  };

  // What the list shows: the loaded requests narrowed by work type and search,
  // then sorted. Search covers what a row shows — number, customer, vehicle,
  // insurer — plus the customer's phone and email.
  const visibleQuotes = (() => {
    const term = search.trim().toLowerCase();

    const rows = quotes.filter((quote) => {
      if (listView.work === 'insurance' && !quote.isInsuranceWork) return false;
      if (listView.work === 'private' && quote.isInsuranceWork) return false;
      if (!term) return true;
      return [
        getRequestNumber(quote),
        quote.panelQuoteJobNumber?.toString(),
        customerDisplayName(quote),
        quote.customerOrganisation?.tradingName,
        quote.customer?.email,
        quote.customer?.phone,
        quote.vehicle?.registration,
        quote.vehicle?.rego,
        getVehicleDescription(quote),
        quote.isInsuranceWork ? resolveInsurerDisplay(quote) : null
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term));
    });

    const byDate = (field, direction) => (a, b) =>
      direction * (new Date(a[field]) - new Date(b[field]));

    switch (listView.sort) {
      case 'oldest':
        return [...rows].sort(byDate('$createdAt', 1));
      case 'updated':
        return [...rows].sort(byDate('$updatedAt', -1));
      case 'customer':
        // Requests with no customer yet go last rather than first.
        return [...rows].sort((a, b) => {
          const nameA = customerDisplayName(a);
          const nameB = customerDisplayName(b);
          if (!nameA || !nameB) return nameA ? -1 : nameB ? 1 : 0;
          return nameA.localeCompare(nameB);
        });
      default:
        return [...rows].sort(byDate('$createdAt', -1));
    }
  })();

  const listNarrowed = Boolean(search.trim()) || listView.work !== DEFAULT_LIST_VIEW.work;

  const truncateText = (text, max = 850) => {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const selectedWorkDetails = selectedQuote
    ? truncateText(selectedQuote.damageDescription || selectedQuote.damage_description || '')
    : '';
  const selectedVehicle = selectedQuote?.vehicle
    || selectedQuote?.customerVehicle
    || selectedQuote?.vehicleDetails
    || selectedQuote?.customer_vehicle
    || selectedQuote?.vehicle_details
    || {};

  const selectedHourlyRate = formatCurrency(selectedQuote?.hourlyRate ?? selectedQuote?.hourly_rate);

  const vehicleRego = priv.rego(selectedVehicle.registration || selectedVehicle.rego || selectedVehicle.Registration) || '—';
  const vehicleYear = selectedVehicle.year || selectedVehicle.Year || '—';
  const vehicleMake = selectedVehicle.make || selectedVehicle.Make || '—';
  const vehicleModel = selectedVehicle.model || selectedVehicle.Model || '—';
  const vehicleVin = priv.vin(selectedVehicle.vin || selectedVehicle.VIN);
  const vehicleOdometer = selectedQuote?.vehicleOdometer || selectedVehicle.odometer || selectedVehicle.mileage;
  const vehicleColour = selectedQuote?.vehicleColour || selectedVehicle.colour || selectedVehicle.color || selectedVehicle.Color;

  const listSkeleton = (count) => (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="h-4 w-4 rounded mt-1" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="flex flex-col h-full p-6 gap-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Quotes</h1>
        <PageHeaderActions
          onCustomerSelect={handleSearchCustomerSelect}
          onVehicleSelect={handleSearchVehicleSelect}
        />
      </div>

      {/* Status on the left, actions on the right — the same row as the Fleet
          page's tabs. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          options={statusFilterOptions}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
        />

        <div className="flex items-center gap-2">
          {panelQuoteEnabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportPanelQuoteForm(true)}
            >
              <Import className="mr-1 h-4 w-4" /> Import
            </Button>
          )}
          <Button
            size="sm"
            onClick={handleCreateQuoteRequest}
            disabled={creatingQuoteRequest}
          >
            <Plus className="mr-1 h-4 w-4" />
            {creatingQuoteRequest ? 'Creating…' : 'New quote request'}
          </Button>
        </div>
      </div>

      {initialLoading ? (
        <div className="grid grid-cols-1 xl:grid-cols-8 gap-4 flex-1 min-h-0">
          <Card className="xl:col-span-3 min-h-0 p-4 space-y-4">
            <Skeleton className="h-9 w-full" />
            {listSkeleton(8)}
          </Card>
          <Card className="xl:col-span-5 min-h-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-9 w-40" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <Skeleton className="h-6 w-32 mb-3" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                  <div>
                    <Skeleton className="h-6 w-24 mb-3" />
                    <div className="space-y-2">
                      <Skeleton className="h-16 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <Skeleton className="h-6 w-24 mb-3" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                  <div>
                    <Skeleton className="h-6 w-20 mb-3" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
          <div className="grid grid-cols-1 xl:grid-cols-8 gap-4 flex-1 min-h-0">
            {/* Quotes List Panel — no heading of its own: the page title says
                what it is. */}
            <Card className="xl:col-span-3 flex flex-col min-h-0">
              <div className="flex-shrink-0 space-y-2 p-4 pb-2">
                <ListToolbar
                  search={search}
                  onSearchChange={setSearch}
                  placeholder="Search customer, vehicle, rego…"
                  sortOptions={SORT_OPTIONS}
                  sort={listView.sort}
                  onSortChange={(sort) => updateListView({ sort })}
                  filterGroups={[
                    {
                      id: 'work',
                      label: 'Work type',
                      options: WORK_OPTIONS,
                      value: listView.work,
                      defaultValue: DEFAULT_LIST_VIEW.work
                    }
                  ]}
                  onFilterChange={(id, value) => updateListView({ [id]: value })}
                />

                {/* Bulk selection: one quiet line rather than a badge, a
                    delete button and a clear button in three places. */}
                {selectedQuoteRequests.size > 0 && (
                  <div className="flex items-center gap-2 rounded-md bg-muted/50 py-1 pl-3 pr-1 text-sm">
                    <span className="min-w-0 flex-1 truncate font-medium">
                      {selectedQuoteRequests.size} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={() => setSelectedQuoteRequests(new Set())}
                    >
                      Clear
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-destructive hover:text-destructive"
                      onClick={handleBulkDelete}
                      disabled={bulkDeleteLoading}
                    >
                      <Trash2 />
                      {bulkDeleteLoading ? 'Deleting…' : 'Delete'}
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 pt-1 pb-4">
                {listLoading && quotes.length === 0 ? (
                  listSkeleton(5)
                ) : visibleQuotes.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">
                      {quotes.length === 0 ? 'No quote requests found.' : 'No quote requests match.'}
                    </p>
                    {quotes.length > 0 && listNarrowed && (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setSearch('');
                          updateListView({ work: DEFAULT_LIST_VIEW.work });
                        }}
                      >
                        Clear search and filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {listLoading && (
                      <div className="text-xs text-muted-foreground">Updating list…</div>
                    )}
                    {visibleQuotes.map((quote) => {
                      const requestNumber = getRequestNumber(quote);
                      const isSelected = selectedQuote?.$id === quote.$id;
                      const isChecked = selectedQuoteRequests.has(quote.$id);
                      return (
                      <Card
                        key={quote.$id}
                        ref={isSelected ? selectedItemRef : undefined}
                        className={cn(
                          'group cursor-pointer transition-colors hover:bg-accent/50',
                          isSelected && 'ring-2 ring-primary',
                          isChecked && 'bg-accent'
                        )}
                        onClick={() => handleQuoteSelect(quote.$id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              className="self-start mt-1"
                              checked={isChecked}
                              onCheckedChange={(checked) => handleSelectQuoteRequest(quote.$id, checked === true)}
                              onClick={(e) => e.stopPropagation()}
                              aria-label={`Select ${requestNumber}`}
                            />
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="text-xs font-semibold text-muted-foreground">
                                {quote.panelQuoteJobNumber ? `Panel Quote #${priv.jobNumber(quote.panelQuoteJobNumber)}` : requestNumber}
                              </div>
                              {quote.customerOrganisation ? (
                                <h3 className="font-semibold truncate">
                                  {quote.customerOrganisation.name || 'Business'}
                                </h3>
                              ) : quote.customer ? (
                                <h3 className="font-semibold truncate">
                                  {quote.customer?.title ? `${quote.customer.title} ` : ''}
                                  {priv.name(`${quote.customer?.firstName || quote.customer?.first_name || ''} ${quote.customer?.lastName || quote.customer?.last_name || ''}`.trim())}
                                </h3>
                              ) : (
                                <h3 className="font-semibold truncate text-muted-foreground italic">
                                  New quote request
                                </h3>
                              )}
                              <p className="text-sm text-muted-foreground truncate">{getVehicleDescription(quote)}</p>
                              {quote.isInsuranceWork && (
                                <p className="text-sm text-muted-foreground truncate">
                                  {resolveInsurerDisplay(quote)}
                                </p>
                              )}
                            </div>
                            {/* Shown on hover; the row itself selects, this opens. */}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 [&_svg]:size-5"
                              aria-label={`Open ${requestNumber}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openQuoteRequest(quote.$id);
                              }}
                            >
                              <ChevronRight />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            {/* Quote Preview Panel */}
            <Card className="xl:col-span-5 flex flex-col min-h-0">
              <CardHeader className="flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-3">
                    {selectedQuote ? `Quote Preview: ${getRequestNumber(selectedQuote)}` : 'Quote Preview'}
                    {selectedQuote && <QuoteStatusBadge status={selectedQuote.status} />}
                  </CardTitle>
                  {selectedQuote && (
                    <Button
                      size="sm"
                      onClick={handleExpandQuote}
                    >
                      <Maximize2 className="mr-1 h-4 w-4" /> Open Quote Request
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto">
                {previewLoading ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                      <div>
                        <Skeleton className="h-6 w-32 mb-3" />
                        <Skeleton className="h-4 w-full mb-2" />
                        <Skeleton className="h-4 w-3/4" />
                      </div>
                      <div>
                        <Skeleton className="h-6 w-24 mb-3" />
                        <div className="space-y-2">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <Skeleton className="h-6 w-24 mb-3" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                        </div>
                      </div>
                      <div>
                        <Skeleton className="h-6 w-20 mb-3" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-4 w-2/3" />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : selectedQuote ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content - 2 columns on large screens */}
                    <div className="lg:col-span-2 space-y-6">
                      {/* Work Details */}
                      <div>
                        <h3 className="font-semibold mb-3">Work Details</h3>
                        <p className="text-sm text-muted-foreground mb-3">
                          {selectedWorkDetails?.trim() ? selectedWorkDetails : 'No damage description provided'}
                        </p>
                        {selectedQuote.isInsuranceWork && (
                          <div className="space-y-1 text-sm">
                            <div className="flex gap-2">
                              <span className="font-medium">Insurance:</span>
                              <span>{resolveInsurerDisplay(selectedQuote)}</span>
                            </div>
                            {selectedQuote.excess && (
                              <div className="flex gap-2">
                                <span className="font-medium">Excess:</span>
                                <span>${selectedQuote.excess}</span>
                              </div>
                            )}
                          </div>
                        )}
                        {selectedHourlyRate && (
                          <div className="flex gap-2 text-sm text-muted-foreground mt-2">
                            <span className="font-medium">Hourly rate:</span>
                            <span>{selectedHourlyRate}/hr</span>
                          </div>
                        )}
                        {selectedQuote.needsLoanVehicle && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                            Loan vehicle required
                          </div>
                        )}
                      </div>

                      {/* Top 2 Quotes */}
                      {selectedQuote.quotes && selectedQuote.quotes.length > 0 && (
                        <div>
                          <h3 className="font-semibold mb-3">Quotes ({selectedQuote.quotes.length})</h3>
                          <div className="space-y-2">
                            {selectedQuote.quotes.slice(0, 2).map((quoteItem, index) => (
                              <Card key={index} className="p-3">
                                <div className="flex items-center justify-between">
                                  <div className="font-semibold">
                                    ${quoteItem.total?.toFixed(2) || '0.00'}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {quoteItem.lines && quoteItem.lines.length > 0 && (
                                      <span>{quoteItem.lines.length} line items</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  Created {formatDate(quoteItem.$createdAt)}
                                </div>
                              </Card>
                            ))}
                          </div>
                          {selectedQuote.quotes.length > 2 && (
                            <p className="text-sm text-muted-foreground mt-2">
                              +{selectedQuote.quotes.length - 2} more quote(s)
                            </p>
                          )}
                        </div>
                      )}

                      {/* Image Gallery */}
                      <div>
                        <PreviewImageThumbnails
                          quoteRequestId={selectedQuote.$id}
                          imageMetadata={selectedQuote.images}
                          maxImages={3}
                        />
                      </div>

                      {/* Status moved to header */}
                    </div>

                    {/* Sidebar - 1 column on large screens */}
                    <div className="space-y-6">
                      {/* Customer Information */}
                      <div>
                        <h3 className="font-semibold mb-3">Customer</h3>
                        <div className="space-y-2 text-sm">
                          {selectedQuote.customerOrganisation ? (
                            <>
                              <div className="flex gap-2">
                                <span className="font-medium text-muted-foreground">Business:</span>
                                <span className="truncate">{selectedQuote.customerOrganisation.name || '—'}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="font-medium text-muted-foreground">Phone:</span>
                                <span>{priv.phone(firstOfJsonArray(selectedQuote.customerOrganisation.phones)) || '—'}</span>
                              </div>
                              <div className="flex gap-2">
                                <span className="font-medium text-muted-foreground">Email:</span>
                                <span>{priv.email(firstOfJsonArray(selectedQuote.customerOrganisation.emails), selectedQuote.customerOrganisation.name) || '—'}</span>
                              </div>
                            </>
                          ) : (
                          <>
                          <div className="flex gap-2">
                            <span className="font-medium text-muted-foreground">Name:</span>
                            <span className="truncate">
                              {selectedQuote.customer?.title ? `${selectedQuote.customer.title} ` : ''}
                              {priv.name(`${selectedQuote.customer?.firstName || selectedQuote.customer?.first_name || ''} ${selectedQuote.customer?.lastName || selectedQuote.customer?.last_name || ''}`.trim()) || '—'}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <span className="font-medium text-muted-foreground">Phone:</span>
                            <span>{priv.phone(selectedQuote.customer?.phone) || '—'}</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="font-medium text-muted-foreground">Email:</span>
                            <span>{priv.email(selectedQuote.customer?.email, selectedQuote.customer?.firstName || selectedQuote.customer?.first_name) || '—'}</span>
                          </div>
                          {(() => {
                            // Dev mode: addresses are blanked, so hide the whole block
                            if (priv.active) return null;
                            const addressParts = [
                              selectedQuote.customer?.street1,
                              selectedQuote.customer?.street2,
                              [selectedQuote.customer?.suburb, selectedQuote.customer?.region, selectedQuote.customer?.postcode]
                                .filter(Boolean)
                                .join(', ')
                            ].filter((part) => part && part.toString().trim().length > 0);
                            if (addressParts.length === 0) return null;
                            return (
                              <div className="space-y-1 text-sm">
                                <div className="font-medium text-muted-foreground">Address:</div>
                                {addressParts.map((line, idx) => (
                                  <div key={idx}>{line}</div>
                                ))}
                              </div>
                            );
                          })()}
                          </>
                          )}
                        </div>
                      </div>

                      {/* Vehicle Information */}
                      <div>
                        <h3 className="font-semibold mb-3">Vehicle</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex gap-2">
                            <span className="font-medium">Rego:</span>
                            <span>{vehicleRego}</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="font-medium">Year:</span>
                            <span>{vehicleYear}</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="font-medium">Make:</span>
                            <span>{vehicleMake}</span>
                          </div>
                          <div className="flex gap-2">
                            <span className="font-medium">Model:</span>
                            <span>{vehicleModel}</span>
                          </div>
                          {vehicleVin && (
                            <div className="flex gap-2">
                              <span className="font-medium">VIN:</span>
                              <span>{vehicleVin}</span>
                            </div>
                          )}
                          {vehicleOdometer && (
                            <div className="flex gap-2">
                              <span className="font-medium">Odometer:</span>
                              <span>{Number(vehicleOdometer).toLocaleString()} km</span>
                            </div>
                          )}
                          {vehicleColour && (
                            <div className="flex gap-2">
                              <span className="font-medium">Colour:</span>
                              <span>{vehicleColour}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-4xl mb-4">📋</div>
                    <p className="text-muted-foreground">Select a quote from the list to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      )}

      {/* Import Panel Quote Modal */}
      {showImportPanelQuoteForm && (
        <ImportPanelQuoteForm
          isOpen={showImportPanelQuoteForm}
          onClose={() => setShowImportPanelQuoteForm(false)}
          onSuccess={handleImportPanelQuoteSuccess}
          onError={showErrorToast}
        />
      )}

      {/* Search result dialogs */}
      <CustomerForm
        customerId={searchCustomerId}
        isOpen={searchCustomerOpen}
        onClose={() => setSearchCustomerOpen(false)}
      />
      <VehicleForm
        vehicleId={searchVehicleId}
        isOpen={searchVehicleOpen}
        onClose={() => setSearchVehicleOpen(false)}
      />

      <DeleteConfirmDialog
        open={Boolean(pendingBulkDelete)}
        onOpenChange={(open) => { if (!open) setPendingBulkDelete(null); }}
        count={pendingBulkDelete?.rows.length || 1}
        onConfirm={confirmBulkDelete}
      />
    </div>
  );
};

export default QuoteRequests;